import { useState, useEffect, useRef, useCallback } from 'react';
import { Play, RefreshCw, ChevronDown, ChevronRight, CheckCircle, XCircle, AlertCircle, Loader2, ShoppingBag } from 'lucide-react';
import {
  createScrapingRun,
  getScrapingRun,
  getScrapingRunItems,
  listScrapingRuns,
} from '../../utils/scrapingApi';
import { useStandardIngredientsStore } from '../../store/useStandardIngredientsStore';
import { Retailer, ScrapingRun, ScrapingRunStatus, ScrapingRunItemResult } from '../../types/retailerProduct';
import { Timestamp } from 'firebase/firestore';

type Scope = 'all' | 'limit' | 'specific';

const RETAILER_OPTIONS: { value: Retailer; label: string }[] = [
  { value: 'carrefour', label: 'Carrefour' },
];

function StatusBadge({ status }: { status: ScrapingRunStatus }) {
  const config: Record<ScrapingRunStatus, { label: string; className: string }> = {
    pending:   { label: 'En attente',  className: 'bg-gray-100 text-gray-700' },
    running:   { label: 'En cours',    className: 'bg-blue-100 text-blue-700' },
    completed: { label: 'Terminé',     className: 'bg-green-100 text-green-700' },
    failed:    { label: 'Échoué',      className: 'bg-red-100 text-red-700' },
    cancelled: { label: 'Annulé',      className: 'bg-gray-100 text-gray-500' },
  };
  const { label, className } = config[status] ?? config.pending;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>
      {label}
    </span>
  );
}

function ProgressBar({ value, total }: { value: number; total: number }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div
        className="bg-menioo-green h-2 rounded-full transition-all duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function RunDetail({ runId }: { runId: string }) {
  const [items, setItems] = useState<ScrapingRunItemResult[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getScrapingRunItems(runId)
      .then(setItems)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [runId]);

  if (loading) return <div className="py-4 text-center text-sm text-gray-500">Chargement...</div>;

  return (
    <div className="mt-3 space-y-1 max-h-60 overflow-y-auto">
      {items.map((item) => (
        <div key={item.ingredientId} className="flex items-center gap-2 text-xs py-1 px-2 rounded hover:bg-gray-50">
          {item.status === 'success' && <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />}
          {item.status === 'no_results' && <AlertCircle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />}
          {item.status === 'error' && <XCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />}
          <span className="flex-1 text-gray-700">{item.ingredientName}</span>
          {item.status === 'success' && (
            <span className="text-green-600">{item.productsFound} produits</span>
          )}
          {item.status === 'no_results' && (
            <span className="text-amber-600">0 résultat</span>
          )}
          {item.status === 'error' && (
            <span className="text-red-600 truncate max-w-[200px]">{item.errorMessage}</span>
          )}
        </div>
      ))}
    </div>
  );
}

function formatDate(ts: Timestamp | Record<string, number> | undefined | null): string {
  if (!ts) return '—';
  // Firestore Timestamp client : .toDate() disponible
  if (typeof (ts as any).toDate === 'function') {
    return (ts as Timestamp).toDate().toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  }
  // Firestore Admin SDK sérialisé en JSON : { _seconds, _nanoseconds } ou { seconds, nanoseconds }
  const seconds = (ts as any)._seconds ?? (ts as any).seconds;
  if (seconds == null) return '—';
  return new Date(seconds * 1000).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

export function RetailerScraping() {
  const [retailer, setRetailer] = useState<Retailer>('carrefour');
  const [scope, setScope] = useState<Scope>('limit');
  const [scopeLimit, setScopeLimit] = useState(10);
  const [scopeIngredientIds, setScopeIngredientIds] = useState<string[]>([]);
  const [ingredientSearch, setIngredientSearch] = useState('');

  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // _activeRunId conservé pour une future action "Annuler le run" (cancel)
  const [_activeRunId, setActiveRunId] = useState<string | null>(null);
  const [activeRun, setActiveRun] = useState<ScrapingRun | null>(null);
  const pollingRef = useRef<number | null>(null);

  const [history, setHistory] = useState<ScrapingRun[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);

  const { ingredients, fetchIngredients } = useStandardIngredientsStore();

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const runs = await listScrapingRuns(retailer);
      setHistory(runs);
    } catch (err) {
      console.error('Erreur chargement historique:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [retailer]);

  useEffect(() => {
    if (ingredients.length === 0) fetchIngredients();
    loadHistory();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stopPolling = useCallback(() => {
    if (pollingRef.current !== null) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startPolling = useCallback((runId: string) => {
    stopPolling();
    pollingRef.current = window.setInterval(async () => {
      try {
        const run = await getScrapingRun(runId);
        setActiveRun(run);
        if (run.status !== 'running' && run.status !== 'pending') {
          stopPolling();
          loadHistory();
        }
      } catch {
        stopPolling();
      }
    }, 2500);
  }, [stopPolling, loadHistory]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const handleLaunch = async () => {
    setError(null);
    setLaunching(true);
    try {
      const params: Parameters<typeof createScrapingRun>[0] = { retailer, scope };
      if (scope === 'limit') params.scopeLimit = scopeLimit;
      if (scope === 'specific') params.scopeIngredientIds = scopeIngredientIds;

      const { runId } = await createScrapingRun(params);
      setActiveRunId(runId);

      const run = await getScrapingRun(runId);
      setActiveRun(run);
      startPolling(runId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lancement run');
    } finally {
      setLaunching(false);
    }
  };

  const filteredIngredients = ingredients.filter(
    (i) =>
      i.status === 'validated' &&
      i.name.toLowerCase().includes(ingredientSearch.toLowerCase())
  );

  const toggleIngredient = (id: string) => {
    setScopeIngredientIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };


  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <ShoppingBag className="h-6 w-6 text-menioo-green" />
        <h2 className="text-xl font-display font-bold text-menioo-navy">
          Scraping Retailer
        </h2>
      </div>

      {/* Configuration */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Configuration du run</h3>

        {/* Sélecteur enseigne */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Enseigne</label>
          <select
            value={retailer}
            onChange={(e) => setRetailer(e.target.value as Retailer)}
            className="block w-48 rounded-md border-gray-300 shadow-sm focus:border-menioo-green focus:ring-menioo-green sm:text-sm"
          >
            {RETAILER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Sélecteur scope */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Périmètre</label>
          <div className="flex gap-3 flex-wrap">
            {(['all', 'limit', 'specific'] as const).map((s) => (
              <label
                key={s}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                  scope === s
                    ? 'border-menioo-green bg-menioo-green/5 text-menioo-green font-medium'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="scope"
                  value={s}
                  checked={scope === s}
                  onChange={() => setScope(s)}
                  className="sr-only"
                />
                {s === 'all' && 'Tous les ingrédients'}
                {s === 'limit' && 'Limité'}
                {s === 'specific' && 'Sélection manuelle'}
              </label>
            ))}
          </div>

          {scope === 'limit' && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-sm text-gray-600">Scraper les</span>
              <input
                type="number"
                min={1}
                max={800}
                value={scopeLimit}
                onChange={(e) => setScopeLimit(Number(e.target.value))}
                className="w-20 rounded-md border-gray-300 shadow-sm focus:border-menioo-green focus:ring-menioo-green sm:text-sm text-center"
              />
              <span className="text-sm text-gray-600">premiers ingrédients validés</span>
            </div>
          )}

          {scope === 'specific' && (
            <div className="mt-3 space-y-2">
              <input
                type="search"
                placeholder="Rechercher un ingrédient..."
                value={ingredientSearch}
                onChange={(e) => setIngredientSearch(e.target.value)}
                className="block w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-menioo-green focus:ring-menioo-green sm:text-sm"
              />
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                {filteredIngredients.slice(0, 50).map((ing) => (
                  <label
                    key={ing.id}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={scopeIngredientIds.includes(ing.id)}
                      onChange={() => toggleIngredient(ing.id)}
                      className="rounded border-gray-300 text-menioo-green focus:ring-menioo-green"
                    />
                    {ing.name}
                    <span className="text-xs text-gray-400">({ing.language})</span>
                  </label>
                ))}
              </div>
              {scopeIngredientIds.length > 0 && (
                <p className="text-xs text-gray-500">{scopeIngredientIds.length} ingrédient(s) sélectionné(s)</p>
              )}
            </div>
          )}
        </div>

        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <button
          onClick={handleLaunch}
          disabled={launching || (scope === 'specific' && scopeIngredientIds.length === 0)}
          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-menioo-green rounded-lg hover:bg-menioo-green-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {launching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Play className="h-4 w-4" />
          )}
          Lancer le scraping
        </button>
      </div>

      {/* Run en cours */}
      {activeRun && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Run en cours</h3>
            <StatusBadge status={activeRun.status} />
          </div>

          <ProgressBar value={activeRun.processedCount} total={activeRun.totalIngredients} />
          <p className="text-sm text-gray-600">
            {activeRun.processedCount} / {activeRun.totalIngredients} ingrédients traités
          </p>

          <div className="flex gap-6 text-sm flex-wrap">
            <span className="flex items-center gap-1.5 text-green-600">
              <CheckCircle className="h-4 w-4" />
              {activeRun.successCount} succès
            </span>
            <span className="flex items-center gap-1.5 text-amber-600">
              <AlertCircle className="h-4 w-4" />
              {activeRun.noResultsCount ?? 0} sans résultat
            </span>
            <span className="flex items-center gap-1.5 text-red-600">
              <XCircle className="h-4 w-4" />
              {activeRun.errorCount} erreurs
            </span>
          </div>

          {(activeRun.status === 'running' || activeRun.status === 'pending') && (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Mise à jour toutes les 2,5s…
            </div>
          )}
        </div>
      )}

      {/* Historique */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Historique des runs</h3>
          <button
            onClick={loadHistory}
            disabled={historyLoading}
            className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${historyLoading ? 'animate-spin' : ''}`} />
            Rafraîchir
          </button>
        </div>

        {historyLoading ? (
          <p className="text-sm text-gray-500">Chargement…</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-gray-500">Aucun run pour l'instant.</p>
        ) : (
          <div className="space-y-2">
            {history.map((run) => (
              <div key={run.id} className="border border-gray-100 rounded-lg">
                <button
                  onClick={() => setExpandedRunId(expandedRunId === run.id ? null : run.id)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {expandedRunId === run.id
                      ? <ChevronDown className="h-4 w-4 text-gray-400" />
                      : <ChevronRight className="h-4 w-4 text-gray-400" />
                    }
                    <div>
                      <span className="text-sm font-medium text-gray-900 capitalize">{run.retailer}</span>
                      <span className="mx-2 text-gray-300">·</span>
                      <span className="text-sm text-gray-500">{formatDate(run.startedAt)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">
                      {run.processedCount}/{run.totalIngredients}
                    </span>
                    <StatusBadge status={run.status} />
                  </div>
                </button>
                {expandedRunId === run.id && (
                  <div className="px-4 pb-3 border-t border-gray-100">
                    <div className="flex gap-4 text-xs text-gray-500 py-2 flex-wrap">
                      <span>Scope : <strong>{run.scope}</strong>{run.scopeLimit ? ` (${run.scopeLimit})` : ''}</span>
                      <span>Déclenché par : <strong>{run.triggeredBy}</strong></span>
                      {run.finishedAt && <span>Terminé : {formatDate(run.finishedAt)}</span>}
                    </div>
                    <RunDetail runId={run.id} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
